import { useEffect, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { Button, type ButtonProps } from '@app/components/ui/button';
import { ROUTES } from '@cloud-ui/constants';
import { useAuth } from '@cloud-ui/contexts/AuthContext';
import { useFusionAuth } from '@cloud-ui/contexts/FusionAuthContext';
import { useToast } from '@cloud-ui/contexts/ToastContext';
import {
  ORGANIZATION_SESSION_MUTATION_KEY,
  subscribeToSessionChanges,
  trackAuthSession,
} from '@cloud-ui/utils/authSessionStorage';
import { tryBrowserStorage } from '@cloud-ui/utils/browserStorage';
import { getPendingInvite } from '@cloud-ui/utils/pendingInvite';

/** Carries an invitation through the existing /logout callback during account switching. */
export const ACCOUNT_SWITCH_INVITE_KEY = 'account_switch_invite_code';

class AccountSwitchStorageError extends Error {
  constructor() {
    super('Unable to save this account switch. Allow browser storage and try again.');
  }
}

/** Verify the handoff before ending the session that still holds its invitation. */
function prepareAccountSwitch(
  inviteCode: string | null,
  returnTo: string | undefined,
  usesFusionAuth: boolean,
): () => void {
  const prepared: { storage: Storage; key: string; value: string; previous: string | null }[] = [];
  const restore = () => {
    for (const { storage, key, value, previous } of prepared) {
      tryBrowserStorage(() => {
        // A failed request must not restore over another tab's newer destination.
        if (storage.getItem(key) === value) {
          if (previous === null) {
            storage.removeItem(key);
          } else {
            storage.setItem(key, previous);
          }
        }
      });
    }
  };
  const save = (storage: Storage, key: string, value: string) => {
    prepared.push({ storage, key, value, previous: storage.getItem(key) });
    storage.setItem(key, value);
    if (storage.getItem(key) !== value) {
      throw new AccountSwitchStorageError();
    }
  };
  try {
    if (returnTo && usesFusionAuth) {
      save(localStorage, 'redirect_after_login', returnTo);
    }
    if (inviteCode) {
      // Local sign-in has no logout callback to restore the invitation. Make the displayed
      // code pending before logout, even when the card could only hold it in memory.
      save(
        usesFusionAuth ? sessionStorage : localStorage,
        usesFusionAuth ? ACCOUNT_SWITCH_INVITE_KEY : 'invite_code',
        inviteCode,
      );
    }
  } catch {
    restore();
    throw new AccountSwitchStorageError();
  }
  return restore;
}

interface AccountRecoveryProps {
  returnTo?: string;
  /**
   * The invite the surrounding page is asking about, which the next account is asked about in
   * turn. Only the invite card sets it. Elsewhere a code this tab holds was never answered, so it
   * is not carried: the next login would redeem it for someone who was never asked.
   */
  inviteCode?: string;
  /** Set while the page is doing something that a logout must not run alongside. */
  disabled?: boolean;
  /** Lets a surrounding form disable competing actions until logout succeeds or fails. */
  onPendingChange?: (pending: boolean) => void;
  /** Retain a reload-only continuation after logout unmounts this button. */
  onNavigationReady?: (destination: string) => void;
  /** How the button looks where it is one of several choices. */
  variant?: ButtonProps['variant'];
  className?: string;
}

/** Ends both application and SSO sessions, including when app sign-in never completed. */
export default function AccountRecovery({
  returnTo,
  inviteCode: askedInviteCode,
  disabled = false,
  onPendingChange,
  onNavigationReady,
  variant,
  className,
}: AccountRecoveryProps) {
  const isMounted = useRef(false);
  const { logout, reloadSession } = useAuth();
  const fusionAuth = useFusionAuth();
  const { showToast } = useToast();
  const [handoff, setHandoff] = useState<{
    destination: string;
    inviteCode: string | null;
    session: ReturnType<typeof trackAuthSession>;
  } | null>(null);

  const discardHandoff = () => {
    if (handoff?.inviteCode) {
      tryBrowserStorage(() => {
        if (sessionStorage.getItem(ACCOUNT_SWITCH_INVITE_KEY) === handoff.inviteCode) {
          sessionStorage.removeItem(ACCOUNT_SWITCH_INVITE_KEY);
        }
      });
    }
    setHandoff(null);
  };

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!handoff) {
      return;
    }
    const unsubscribe = subscribeToSessionChanges((type) => {
      if (type === 'auth_logout_event') {
        discardHandoff();
      }
    });
    if (!handoff.session.isCurrent()) {
      discardHandoff();
    }
    return () => {
      unsubscribe();
      handoff.session.dispose();
    };
  }, [handoff]);

  const switchAccount = useMutation({
    mutationKey: ORGANIZATION_SESSION_MUTATION_KEY,
    onMutate: () => onPendingChange?.(true),
    mutationFn: async () => {
      const usesFusionAuth = fusionAuth?.fusionAuthConfig.useFusionAuth;
      const loginUrl = returnTo
        ? `${ROUTES.login}?${new URLSearchParams({ redirect: returnTo })}`
        : ROUTES.login;
      const logoutUrl = usesFusionAuth ? fusionAuth.buildLogoutUrl() : loginUrl;
      if (!logoutUrl) {
        throw new Error('Logout is not configured');
      }

      // The invite to carry to the next account: the one the page is asking about, else the
      // pending one, which the login that led here sent. It is read first, because the logout
      // that FusionAuth needs forgets every invite in this browser.
      const inviteCode = askedInviteCode ?? getPendingInvite();
      const restorePrepared = prepareAccountSwitch(inviteCode, returnTo, Boolean(usesFusionAuth));
      try {
        await logout({ preserveInvitation: !usesFusionAuth });
      } catch (error) {
        restorePrepared();
        throw error;
      }

      // SSO logout preserves the verified handoff; local logout preserves the verified pending
      // invitation. Do not delete and rewrite them after the session ends.
      if (!usesFusionAuth || !inviteCode) {
        tryBrowserStorage(() => sessionStorage.removeItem(ACCOUNT_SWITCH_INVITE_KEY));
      }

      // A full navigation also discards cached data belonging to the previous account.
      if (onNavigationReady) {
        // Register retains its continuation when logout unmounts the invitation card.
        onNavigationReady(logoutUrl);
      } else if (isMounted.current) {
        // Standalone recovery pages stay mounted if navigation is canceled. Keep the prepared
        // handoff rather than logging out again and consuming already-cleared invitation state.
        setHandoff({
          destination: logoutUrl,
          inviteCode: usesFusionAuth ? inviteCode : null,
          session: trackAuthSession(),
        });
      }
      if (usesFusionAuth && inviteCode) {
        reloadSession(logoutUrl, {
          // The refresh boundary unmounts this caller. A later logout must still discard
          // this handoff, without removing one prepared by another account switch.
          onSessionEnd: () => {
            tryBrowserStorage(() => {
              if (sessionStorage.getItem(ACCOUNT_SWITCH_INVITE_KEY) === inviteCode) {
                sessionStorage.removeItem(ACCOUNT_SWITCH_INVITE_KEY);
              }
            });
          },
        });
      } else {
        reloadSession(logoutUrl);
      }
    },
    onError: (error) => {
      console.error('Unable to switch accounts:', error);
      showToast(
        error instanceof AccountSwitchStorageError
          ? error.message
          : 'Unable to switch accounts. Please try again.',
        'error',
      );
    },
    onSettled: () => onPendingChange?.(false),
  });

  return (
    <Button
      onClick={() => {
        if (handoff) {
          if (handoff.session.isCurrent()) {
            reloadSession(handoff.destination);
          } else {
            discardHandoff();
          }
        } else {
          switchAccount.mutate();
        }
      }}
      disabled={switchAccount.isPending || disabled}
      variant={variant}
      className={className}
    >
      {switchAccount.isPending
        ? 'Signing out...'
        : handoff
          ? 'Continue switching accounts'
          : 'Use another account'}
    </Button>
  );
}
